// Which loaded entry ModuleHost may render for a manifest, pure so node --test can reach it.
//
// ModuleHost keeps its entry in state tagged with the module id it was loaded for. A
// state left over from ANOTHER module must never be rendered under this manifest: that
// would mount the previous module's Provider, header actions and routes for a frame, and
// their mount effects (DraftsProvider's fetch, for one) would fire under the wrong module.
// Shell.jsx keys each module's element by id, so this is the second guard, not the only one.

import { withTimeout } from './with-timeout.js';

/**
 * @param {{id: string, entry: object|null, error: unknown}} state  ModuleHost's state
 * @param {string} manifestId  the module being rendered now
 * @param {Map<string, object>} cache  entries already loaded, by module id
 */
export function entryStateFor(state, manifestId, cache) {
  if (state.id === manifestId) return state;
  return { id: manifestId, entry: cache.get(manifestId) ?? null, error: null };
}

/**
 * ModuleHost's page boundary, as a getDerivedStateFromProps step: a crash stays shown
 * only while the person stays on the page that crashed. Navigating to any other path in
 * the module clears it, so one broken page never bricks the whole module.
 *
 * Reset rather than a `key={pathname}` on the boundary: a key would remount every page
 * of the module on every in-module navigation (react-router otherwise keeps a page
 * mounted from /tickets/1 to /tickets/2), throwing away its state and refetching.
 *
 * @param {{failed: boolean, resetKey: string}} state  the boundary's state
 * @param {string} resetKey  the current pathname
 * @returns {{failed: boolean, resetKey: string}|null}  null: no change
 */
export function boundaryStateFor(state, resetKey) {
  return state.resetKey === resetKey ? null : { failed: false, resetKey };
}

/**
 * Loads a module's entry with a deadline (R14). The returned promise answers ONCE: the
 * entry, the load's own error, or LoadTimeoutError at `ms`. A success that arrives after
 * the deadline is still put in `cache`, so the next open of the module (a remount, after
 * navigating away and back) is instant; it never flips a page that already shows
 * "Couldn't load", because that page's answer was already given.
 *
 * @param {() => Promise<{default: object}>} load  the manifest's load()
 * @param {string} id  the module id, the cache key
 * @param {Map<string, object>} cache  entries already loaded, by module id
 * @param {number} ms  the deadline
 */
export function loadModuleEntry(load, id, cache, ms) {
  // Through a resolved promise, so a load() that throws synchronously is a rejection
  // like any other rather than an exception out of the effect.
  const loading = Promise.resolve().then(load).then((mod) => {
    cache.set(id, mod.default);
    return mod.default;
  });
  return withTimeout(loading, ms);
}
